import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Home, ClipboardList, Gauge, Columns3, Scale, Grid2x2, LineChart, Route, Sparkles, FileText, Settings2, LogOut, Menu, X } from "lucide-react";
import { useStrategy } from "@/context/StrategyContext";
import StrategyEntry from "./StrategyEntry";
import { Avatar, DemoBadge, Loading } from "./ui";
import "@/strategy.css";

const NAV = [
  { to: "/roadmap", label: "Home", icon: Home, end: true },
  { to: "/roadmap/assess", label: "My assessment", icon: ClipboardList },
  { to: "/roadmap/alignment", label: "Alignment", icon: Gauge },
  { to: "/roadmap/perspectives", label: "Perspectives", icon: Columns3 },
  { to: "/roadmap/decisions", label: "Decisions", icon: Scale },
  { to: "/roadmap/opportunities", label: "Opportunities", icon: Grid2x2 },
  { to: "/roadmap/revenue", label: "Revenue model", icon: LineChart },
  { to: "/roadmap/plan", label: "Roadmap", icon: Route },
  { to: "/roadmap/adviser", label: "AI adviser", icon: Sparkles },
  { to: "/roadmap/report", label: "Report", icon: FileText },
  { to: "/roadmap/admin", label: "Rounds & admin", icon: Settings2 },
];

function Nav({ onNavigate }) {
  return (
    <nav className="space-y-0.5">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate} data-testid={`st-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) => `st-nav-link${isActive ? " active" : ""}`}>
          <n.icon size={17} strokeWidth={1.6} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

function RoundSwitcher() {
  const { rounds, roundId, setRoundId, round } = useStrategy();
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <select value={roundId || ""} onChange={(e) => setRoundId(e.target.value)} className="st-select !py-2 !text-xs" data-testid="round-switcher">
        {rounds.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      {round?.demo && <span className="whitespace-nowrap"><DemoBadge /></span>}
    </div>
  );
}

export default function StrategyLayout() {
  const { me, logout } = useStrategy();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  if (me === null) return <div className="st-root"><Loading label="Opening Strategy Intelligence" /></div>;
  if (me === false) return <StrategyEntry />;

  const brand = (
    <div className="px-3 pt-1 pb-5">
      <p className="text-xl font-extrabold tracking-tight leading-none"><span className="st-grad-text">GROWTH</span></p>
      <p className="text-xs font-bold mt-1.5 tracking-wide">Strategy Intelligence</p>
      <p className="st-faint text-[11px] mt-1 leading-snug">Building the Intelligence Behind Better Business</p>
    </div>
  );
  const user = (
    <div className="mt-auto pt-4 border-t" style={{ borderColor: "var(--st-line)" }}>
      <div className="flex items-center gap-3 px-2">
        <Avatar pid={me.id} size={32} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold leading-tight">{me.name}</p>
          <p className="st-faint text-[11px] truncate">{me.role_title}</p>
        </div>
        <button onClick={logout} className="st-btn st-btn-quiet !p-2" title="Sign out" data-testid="st-logout"><LogOut size={16} /></button>
      </div>
    </div>
  );

  return (
    <div className="st-root min-h-screen flex">
      <aside className="hidden lg:flex w-[268px] shrink-0 h-screen sticky top-0 flex-col px-4 py-5" style={{ background: "rgba(8,11,16,.6)", borderRight: "1px solid var(--st-line)", backdropFilter: "blur(14px)" }}>
        {brand}
        <div className="px-1 mb-4"><RoundSwitcher /></div>
        <Nav />
        {user}
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3" style={{ background: "rgba(8,11,16,.85)", borderBottom: "1px solid var(--st-line)", backdropFilter: "blur(14px)" }}>
          <p className="font-extrabold tracking-tight"><span className="st-grad-text">GROWTH</span> <span className="text-xs font-bold ml-1">Strategy</span></p>
          <div className="flex items-center gap-2">
            <div className="w-[160px]"><RoundSwitcher /></div>
            <button onClick={() => setOpen(true)} className="st-btn st-btn-ghost !p-2" data-testid="st-mobile-menu"><Menu size={18} /></button>
          </div>
        </header>
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 st-root flex flex-col px-4 py-5" data-testid="st-mobile-drawer">
            <div className="flex items-center justify-between mb-3">{brand}<button onClick={() => setOpen(false)} className="st-btn st-btn-ghost !p-2"><X size={18} /></button></div>
            <Nav onNavigate={() => setOpen(false)} />
            {user}
          </div>
        )}
        <main className="flex-1 px-5 sm:px-8 lg:px-12 py-8 lg:py-10 max-w-[1400px] w-full mx-auto" key={pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
